import React, { useContext } from "react";
import { Dropdown, Avatar, Button, Typography, Space, Flex } from "antd";
import {
  BellOutlined,
  CheckCircleOutlined,
  LogoutOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import useAuth from "../../hooks/useAuth";
import { PATH_DASHBOARD } from "../../routes/path";
import { Images } from "../../common/images";
import type { MenuProps } from "antd";
import { TopHeaderBarProps } from "./topbar.interface";
import { profileRole, profileSection, subHeadingStyle } from "./topbar.styles";
import { useGetOrganizationDetailsQuery } from "../../redux/api/organization/organization";
import { useGetProfileDetailsQuery } from "../../redux/api/profile/profile";
import { upperCase } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { useLazyGetSignedSamlResponseQuery } from "../../redux/api/analytics/analytics";
import { captureException } from "@sentry/browser";
import { SpinnerContext } from "../../context/spinner/spinner";

const { Text } = Typography;

const TopHeaderBar: React.FC<TopHeaderBarProps> = ({
  showDrawer,
  showAlerts,
  activeMenu,
}) => {
  const { logout } = useAuth();
  const { data: organization } = useGetOrganizationDetailsQuery();
  const { data: user } = useGetProfileDetailsQuery();
  const [trigger] = useLazyGetSignedSamlResponseQuery();
  const spinnerContext = useContext(SpinnerContext);
  const openZoho = async () => {
    try {
      spinnerContext?.showSpinner();
      const payload = await trigger().unwrap();
      if (
        !payload ||
        !payload?.SeviceProviderAcsPostUrl ||
        !payload?.SAMLAssertion ||
        !payload?.RelayState
      )
        return;

      // Create a hidden form
      const form = document.createElement("form");
      form.method = "POST";
      form.action = payload.SeviceProviderAcsPostUrl;
      form.target = "_blank"; // Open the form submission in a new tab

      // Add the access token as a hidden input field
      const samlResponseInput = document.createElement("input");
      samlResponseInput.type = "hidden";
      samlResponseInput.name = "SAMLResponse"; // Replace with the actual name of the token header
      samlResponseInput.value = payload.SAMLAssertion;
      form.appendChild(samlResponseInput);

      // Add the access token as a hidden input field
      const relayStateInput = document.createElement("input");
      relayStateInput.type = "hidden";
      relayStateInput.name = "RelayState"; // Replace with the actual name of the token header
      relayStateInput.value = payload.RelayState;
      form.appendChild(relayStateInput);

      // Append the form to the document and submit it
      document.body.appendChild(form);
      form.submit();

      // Remove the form from the document after submission (optional)
      document.body.removeChild(form);
    } catch (error) {
      console.log(error);
      captureException(error);
    }
    spinnerContext?.hidespinner();
  };

  const headingStyle = {
    color: "#7C4095",
    fontWeight: "700",
    fontSize: "16px",
    lineHeight: "14px",
  };

  const items: MenuProps["items"] = [
    {
      key: "email",
      label: (
        <Space style={profileSection}>
          <Avatar>
            {upperCase(
              user?.firstName?.charAt(0) + "" + user?.lastName?.charAt(0)
            )}
          </Avatar>

          <Flex vertical={true}>
            <Text
              style={{ fontSize: "16px", color: "#fff", fontWeight: "600" }}
            >
              {organization?.name}
            </Text>
            <Text style={subHeadingStyle}>{user?.email}</Text>
          </Flex>
        </Space>
      ),
    },
    {
      type: "divider",
      style: { height: "1px", background: "#7C4095" },
    },
    {
      key: "organization",
      label: (
        <a href="/org" style={headingStyle}>
          Organization
        </a>
      ),
    },
    {
      key: "general",
      label: (
        <span style={{ color: "#fff" }}>
          <a href="/org" style={subHeadingStyle}>
            <img src={Images.setting} />
            General
          </a>
        </span>
      ),
    },
    {
      key: "members",
      label: (
        <span style={{ color: "#fff" }}>
          <a href="/manageadmin" style={subHeadingStyle}>
            <img src={Images.userGroup} />
            Members
          </a>
        </span>
      ),
    },
    {
      key: "rolesandpermissions",
      label: (
        <span style={{ color: "#fff" }}>
          <a href={PATH_DASHBOARD.roles.list} style={subHeadingStyle}>
            <img src={Images.userWithKey} /> Roles and Permissions
          </a>
        </span>
      ),
    },
    {
      type: "divider",
      style: { height: "1px", background: "#7C4095" },
    },
    {
      key: "account",
      label: <span style={headingStyle}>Account</span>,
    },
    {
      key: "profile",
      label: (
        <span style={{ color: "#fff" }}>
          <a href="/settings/profile" style={subHeadingStyle}>
            <img src={Images.userIcon} />
            Profile
          </a>
        </span>
      ),
    },
    /*   {
      key: "support",
      label: (
        <span style={{ color: "#fff" }}>
          <a href="/support" style={subHeadingStyle}>
            <img src={Images.support} /> Support
          </a>
        </span>
      ),
    }, */
    {
      type: "divider",
      style: {
        height: "1px",
        background: "#7C4095",
        display: !organization?.organizationServiceLimits?.zohoAnalytics
          ? "none"
          : "",
      },
    },
    ...(!!organization?.organizationServiceLimits?.zohoAnalytics
      ? [
          {
            key: "zoho",
            label: (
              <span style={{ color: "#fff" }} onClick={openZoho}>
                <PieChartOutlined
                  style={{ marginRight: "8px", marginLeft: "2px" }}
                />
                Open Zoho
                <FontAwesomeIcon
                  style={{ marginRight: "0", marginLeft: "12px" }}
                  icon={faArrowUpRightFromSquare}
                />
              </span>
            ),
          },
        ]
      : []),
    {
      type: "divider",
      style: { height: "1px", background: "#7C4095" },
    },
    {
      key: "signout",
      label: (
        <span
          style={{
            color: "#598BEB",
            fontWeight: "700",
            fontSize: "16px",
            lineHeight: "14px",
          }}
          onClick={logout}
        >
          <LogoutOutlined style={{ marginRight: "8px", marginLeft: "2px" }} />{" "}
          Sign Out
        </span>
      ),
    },
  ];

  const organizationMenu: MenuProps = {
    items,
    style: {
      backgroundColor: "#521262",
    },
  };

  return (
    <Flex style={{ width: "100%" }} justify="space-between" align="center">
      <img
        src={Images.logo}
        height={64}
        width={200}
        alt="Logo"
        className="logo"
      />
      <Space direction="horizontal">
        <Button
          type="link"
          onClick={showDrawer}
          icon={<CheckCircleOutlined style={{ color: "#2B6BE6" }} />}
        >
          My Tasks
        </Button>
        |
        <Button
          type="link"
          onClick={showAlerts}
          icon={<BellOutlined style={{ color: "#2B6BE6" }} />}
        >
          My alerts
        </Button>
        <Dropdown menu={organizationMenu}>
          <Space style={profileSection}>
            <Avatar>
              {upperCase(
                user?.firstName?.charAt(0) + "" + user?.lastName?.charAt(0)
              )}
            </Avatar>
            <Space style={profileRole}>
              <Text
                style={{
                  margin: "5px 0",
                  color: "rgba(81, 81, 91, 1)",
                }}
              >
                {organization?.name}
              </Text>
              <Text
                style={{
                  color: "rgba(138, 146, 166, 1)",
                  fontSize: "13px",
                }}
              >
                {user?.role} <img src={Images.downArrow} />
              </Text>
            </Space>
          </Space>
        </Dropdown>
      </Space>
    </Flex>
  );
};

export default TopHeaderBar;
